<template>
  <div
    v-if="current_profile.type !== PROFILE_TYPES.EMPLOYEE"
    :class="{ 'items-center justify-center': !show_name, 'items-center gap-3 bg-gray-50 p-3': show_name }"
    class="flex w-full cursor-pointer rounded-lg hover:bg-gray-100"
    data-test="dropdown-general-user"
    @click="toggle"
  >
    <div class="flex items-center justify-center">
      <Avatar
        :src="current_profile.attributes.avatar_url"
        :username="current_profile.attributes.name"
        class="shrink-0"
        size="32"
      />
    </div>
    <div
      v-if="show_name"
      class="truncate font-semibold text-gray-500"
    >
      {{ current_profile.attributes.first_name }}
    </div>
  </div>

  <div v-else>
    <div
      data-test="dropdown-general-user"
      @click="toggle"
    >
      <Avatar
        :src="current_profile.attributes.avatar_url"
        :username="current_profile.attributes.name"
        class="cursor-pointer"
        class_props="bg-white has-text-primary"
        size="25"
      />
    </div>
  </div>

  <PMenu
    id="overlay_menu"
    ref="menu"
    :model="items"
    :popup="true"
  >
    <template #item="{ item, props }">
      <AppLink
        v-if="item.action === 'profile'"
        :to="{ name: item.route }"
        @click="handleProfileClick"
      >
        <div
          :data-test="item.data_test"
          v-bind="props.action"
        >
          {{ $t(`${item.label}`) }}
        </div>
      </AppLink>
      <div v-else-if="item.action === 'selectTenantModal' && has_multiple_profiles">
        <a
          data-test="select-company"
          v-bind="props.action"
          @click="selectTenant"
        >
          <div
            v-ripple
            class="p-link border-noround relative flex w-full items-center overflow-hidden"
          >
            <PAvatar
              v-if="current_client"
              :image="current_client.attributes.picture_logo_url_m"
              class="mr-3"
              size="large"
            />
            <span class="font-bold"> {{ $t('navbar.topnavbar.selectTeam') }}</span>
          </div>
        </a>
      </div>

      <a
        v-else-if="item.action === 'logout'"
        data-test="logout"
        v-bind="props.action"
        @click="handleLogout"
      >
        <div>
          {{ $t('navbar.topnavbar.logout') }}
        </div>
      </a>
      <a
        v-if="
          item.action === 'stopimpersonation' &&
          current_is_impersonation &&
          current_profile_true.type === PROFILE_TYPES.SUPERVISOR
        "
        v-bind="props.action"
        @click="stopImpersonation"
      >
        <AppIcon
          name="solid-person-to-portal"
          class="transform transition-transform duration-200 hover:animate-pulse"
          size="lg"
        />
        Go back to supervisor land
      </a>
    </template>
  </PMenu>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { useStore } from 'vuex';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import { ROUTES_CUSTOMERS, ROUTES_EMPLOYEES, ROUTES_PARTNERS } from '@/app/data/route_constants';
import useImpersonation from '@/app/util/impersonation';
import SelectTenantModal from '@/app/shared_components/modals/SelectTenantModal.vue';

const { proxy: app } = getCurrentInstance();

const props = defineProps({
  show_name: {
    type: Boolean,
    default: true,
  },
  is_mobile: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close-sidebar']);

const menu = ref(null);

const store = useStore();

const { stopImpersonation } = useImpersonation();

const current_is_impersonation = computed(() => store.getters.current_is_impersonation);
const current_profile = computed(() => store.getters.current_profile);
const current_client = computed(() => store.getters.current_client);
const current_profile_true = computed(() => store.getters.current_profile_true);
const current_project = computed(() => store.getters.current_project);

const routeProfileName = computed(() => {
  switch (current_profile.value.type) {
    case PROFILE_TYPES.CUSTOMER: {
      return ROUTES_CUSTOMERS.SETTINGS_PROFILE;
    }
    case PROFILE_TYPES.EMPLOYEE: {
      return ROUTES_EMPLOYEES.SETTINGS_PROFILE;
    }
    case PROFILE_TYPES.PARTNER: {
      return ROUTES_PARTNERS.SETTINGS_PROFILE;
    }
    default: {
      return '';
    }
  }
});

const routeProfileParams = computed(() =>
  current_profile.value.type === PROFILE_TYPES.CUSTOMER ? { project_id: _.get(current_project.value, 'id', 0) } : {},
);

const items = computed(() => [
  {
    label: 'navbar.topnavbar.selectTeam',
    action: 'selectTenantModal',
  },
  {
    label: 'navbar.topnavbar.profile',
    action: 'profile',
    route: routeProfileName.value,
    params: routeProfileParams.value,
    data_test: 'profile',
  },
  {
    label: 'navbar.topnavbar.logout',
    action: 'logout',
  },
  {
    label: 'contact.actions.impersonateStop',
    action: 'stopimpersonation',
  },
]);

const has_multiple_profiles = computed(() => {
  if (current_is_impersonation.value && current_profile_true.value.type === PROFILE_TYPES.EMPLOYEE) return false;
  return current_profile_true.value.attributes.profiles_count > 1;
});

const toggle = (event) => {
  menu.value.toggle(event);
};

const selectTenant = () => {
  if (app.$buefy) {
    app.$buefy.modal.open({
      component: SelectTenantModal,
    });
  }
};

const handleProfileClick = () => {
  if (props.is_mobile) {
    emit('close-sidebar');
  }
};

const handleLogout = () => {
  if (app.emitter) {
    app.emitter.$emit('logout');
  }
};
</script>
